import * as React from 'react';
import {generatePath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import type {FilterObject} from 'nutshell-core/types';

import {ButtonGroup} from 'shells/button-group/button-group';
import {PageControlBar} from 'shells/control-bar/page-control-bar';
import {MessagePerformanceListItem} from 'shells/drip-sequence-performance/message-performance-list-item';
// @ts-expect-error - imported from .js/.jsx file
import {AudienceIcon} from 'shells/icon/svg/audience-icon';
import {MessageIcon} from 'shells/icon/svg/message-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ReportsIcon} from 'shells/icon/svg/reports-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {LoadingPage} from 'shells/loading-page/loading-page';

import type {
    StartTriggerSetInput,
    DripSequenceGoalSetInput,
    SmsDripEditionFragment as SmsDripEdition,
} from 'nutshell-graphql-types';

import {ControlButtons} from '../../../drip-sequences/drip-sequence-page/control-buttons';
import {DripSequenceEngagement} from '../../../drip-sequences/engagement/drip-sequence-engagement';
import {useDripSequenceGoalSet} from '../../../drip-sequences/graphql/hooks/use-drip-sequence-goal-set';
import {getDripSequenceStatusBarInfo} from '../../../drip-sequences/helpers';
import {DripSequenceRecipientList} from '../../../drip-sequences/recipient-list/drip-sequence-recipient-list';

import {appHistory} from '../../../../../history';
import {PageContainer, PageTitleBar} from '../../../../layout';
import type {ReportURLParams} from '../../../../reports/report-types';

import {DRIP_RECIPIENT_STATUS_FILTERS} from '../../consts';
import {EngagementBar} from '../../engagement/engagement-bar';

import {
    useGetSmsDripSequence,
    useCreateSmsDripSequenceEdition,
    useGetSmsDripSequenceEditions,
    useUpdateSmsDripSequence,
    useDeleteSmsDripEdition,
    useSetDripSequenceStartTrigger,
    useUpdateSmsDripEdition,
} from '../graphql/hooks';
import {dripSequenceFieldFilterFunction} from '../helpers';

import {DripSequenceMessage} from './drip-sequence-message';

type Props = ContextRouter & {
    isArchived?: boolean;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onRemoveAllFilters: () => void;
};

export function DripSequencePage(props: Props) {
    const activeTab: string = props.match.params.tab || 'details';

    const {
        smsDripSequence,
        isLoading: isLoadingDripSequence,
        refetch: refetchDripSequence,
    } = useGetSmsDripSequence(props.match.params.dripSequenceId);

    const {
        editions,
        isLoading: isLoadingEditions,
        refetch: refetchEditions,
    } = useGetSmsDripSequenceEditions({
        dripSequenceId: props.match.params.dripSequenceId,
        limit: 50,
    });
    const {createDripEdition} = useCreateSmsDripSequenceEdition();
    const {updateSmsDripSequence} = useUpdateSmsDripSequence();
    const {deleteDripEdition} = useDeleteSmsDripEdition();
    const {setDripSequenceStartTrigger} = useSetDripSequenceStartTrigger();
    const {dripSequenceGoalSet} = useDripSequenceGoalSet();
    const {updateEdition} = useUpdateSmsDripEdition();

    const handleCreateDripEdition = () => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        smsDripSequence &&
            createDripEdition({campaignId: smsDripSequence.id}).then((result) => {
                if (result && result.data && result.data.smsDripEditionCreate) {
                    appHistory.push(result.data.smsDripEditionCreate.edition.htmlUrlPath);
                }
            });
    };

    const handleSetDripSequenceSetTrigger = (input: StartTriggerSetInput) => {
        return setDripSequenceStartTrigger(input).then(() => {
            refetchDripSequence();
        });
    };

    const handleSetDripSequenceGoal = (input: DripSequenceGoalSetInput) => {
        return dripSequenceGoalSet(input).then(() => {
            refetchDripSequence();
        });
    };

    const isDraft = smsDripSequence && smsDripSequence.status === 'DRAFT';
    const title = smsDripSequence ? smsDripSequence.name : 'Drip sequence';

    const tabOptions = [
        {
            id: 'details',
            label: 'Details',
            icon: MessageIcon,
            href: generatePath(props.match.path, {
                dripSequenceId: props.match.params.dripSequenceId,
                tab: 'details',
            }),
            component: smsDripSequence ? (
                <DripSequenceMessage
                    dripSequence={smsDripSequence}
                    editions={editions}
                    isLoading={isLoadingEditions}
                    isDraft={isDraft || false}
                    refetchDripSequence={refetchDripSequence}
                    refetchDripEditions={refetchEditions}
                    onCreateEdition={handleCreateDripEdition}
                    onDripSequenceUpdate={updateSmsDripSequence}
                    onRemoveEdition={deleteDripEdition}
                    onEditionUpdate={updateEdition}
                    onSetStartTrigger={handleSetDripSequenceSetTrigger}
                    onSetGoal={handleSetDripSequenceGoal}
                />
            ) : (
                <LoadingPage />
            ),
        },
        {
            id: 'engagement',
            label: 'Engagement',
            icon: ReportsIcon,
            href: generatePath(props.match.path, {
                dripSequenceId: props.match.params.dripSequenceId,
                tab: 'engagement',
            }),
            component: (
                <>
                    {isLoadingDripSequence || isLoadingEditions ? (
                        <LoadingPage />
                    ) : (
                        <DripSequenceEngagement
                            dripSequence={smsDripSequence}
                            editions={editions}
                            engagementBar={
                                <EngagementBar
                                    engagementStats={smsDripSequence.engagementStats}
                                    label='Engagement'
                                    isLoading={isLoadingDripSequence}
                                />
                            }
                            renderRow={(edition: SmsDripEdition) => {
                                const isDraftEdition = edition.status === 'NEW';
                                const engagementStats = edition.engagementStats;

                                return (
                                    <MessagePerformanceListItem
                                        name={edition.name}
                                        htmlUrlPath={edition.htmlUrlPath}
                                        isDraft={isDraftEdition}
                                        content={edition.content}
                                        metricStats={{
                                            totalSentCount: engagementStats.totalSentCount,
                                            responseRate: engagementStats.responseRate,
                                        }}
                                        metricKeys={['totalSentCount', 'responseRate']}
                                        statItems={[
                                            {
                                                stat: {
                                                    rate: engagementStats.dripProgressionsGoalMetRate,
                                                    count: engagementStats.goalMetCount,
                                                    linkTo: '#', // TODO: Replace with link to this engagement stat
                                                },
                                                nounSingular: 'Goal met',
                                                nounPlural: 'Goals met',
                                                isLoading: false,
                                            },
                                            {
                                                stat: {
                                                    rate: engagementStats.optoutRate,
                                                    count: engagementStats.optoutCount,
                                                    linkTo: '#',
                                                },
                                                nounSingular: 'Opt-out',
                                                nounPlural: 'Opt-outs',
                                                isLoading: false,
                                            },
                                            {
                                                stat: {
                                                    rate: engagementStats.undeliveredRate,
                                                    count: engagementStats.undeliveredCount,
                                                    linkTo: '#',
                                                },
                                                nounSingular: 'Undeliverable',
                                                nounPlural: 'Undeliverables',
                                                isLoading: false,
                                            },
                                        ]}
                                    />
                                );
                            }}
                        />
                    )}
                </>
            ),
        },
    ];

    if (!isDraft) {
        tabOptions.push({
            id: 'recipients',
            label: 'Recipients',
            icon: AudienceIcon,
            href: generatePath(props.match.path, {
                dripSequenceId: props.match.params.dripSequenceId,
                tab: 'recipients',
            }),
            component: (
                <DripSequenceRecipientList
                    dripSequence={smsDripSequence}
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    urlParams={props.urlParams}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    // @ts-expect-error
                    statusFilters={DRIP_RECIPIENT_STATUS_FILTERS}
                    fieldFilterFunction={dripSequenceFieldFilterFunction}
                />
            ),
        });
    }

    return (
        <PageContainer
            title={title}
            pageTitleBar={
                <PageTitleBar
                    title={title}
                    subheaderPrimaryText='Edit your drip sequence'
                    noMaxWidth={true}
                    breadcrumbs={[
                        {
                            to: props.isArchived
                                ? '/email-marketing/sms/drip-sequences/archived'
                                : '/email-marketing/sms/drip-sequences',
                            text: 'Drip sequences',
                        },
                    ]}
                    editInPlaceProps={{
                        onChange: (newValue: string | null | undefined) => {
                            return smsDripSequence
                                ? updateSmsDripSequence({
                                      id: smsDripSequence.id,
                                      name: newValue,
                                  })
                                : undefined;
                        },
                        placeholder: 'Name this drip sequence',
                    }}
                    buttons={
                        <ButtonGroup>
                            <ControlButtons
                                isLoading={isLoadingDripSequence || isLoadingEditions}
                                editions={editions}
                                dripSequence={smsDripSequence}
                                refetchDripSequence={refetchDripSequence}
                                refetchDripEditions={refetchEditions}
                            />
                        </ButtonGroup>
                    }
                />
            }
            controlBar={
                <PageControlBar
                    statusInfo={
                        smsDripSequence ? getDripSequenceStatusBarInfo(smsDripSequence) : undefined
                    }
                    tabs={{
                        selectedTab: activeTab,
                        options: tabOptions,
                    }}
                />
            }
        >
            {/* @ts-expect-error */}
            <TabbedContent selectedTab={activeTab} tabOptions={tabOptions} />
        </PageContainer>
    );
}
